import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const PRESETS = [
    { id: 'today', label: 'Today', advanced: true },
    { id: '7', label: '7 days' },
    { id: '14', label: '14 days' },
    { id: '30', label: '30 days' },
    { id: '90', label: '90 days', advanced: true },
    { id: '365', label: '365 days', advanced: true },
    { id: 'custom', label: 'Custom dates', advanced: true },
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
// Heat maps get unreadable with many rows: beyond this many days they group by weekday.
export const HEATMAP_MAX_DATE_ROWS = 31;

export function dateLimits(timeZone, advanced, now = dayjs()) {
    const today = now.tz(timeZone).format('YYYY-MM-DD');
    const yesterday = dayjs(today).subtract(1, 'day');
    return {
        max: advanced ? today : yesterday.format('YYYY-MM-DD'),
        min: advanced ? undefined : yesterday.subtract(1, 'month').format('YYYY-MM-DD'),
        yesterday: yesterday.format('YYYY-MM-DD'),
        today,
    };
}

export function presetDates(preset, timeZone, advanced) {
    const limits = dateLimits(timeZone, advanced);
    const end = preset === 'today' ? limits.max : limits.yesterday;
    let start = preset === 'today' ? end : dayjs(end).subtract(Number(preset) - 1, 'day').format('YYYY-MM-DD');
    if (limits.min && start < limits.min) start = limits.min;
    return { start, end };
}

// "8 pm", "12 am" ...
export function hourName(hour) {
    return `${hour % 12 || 12} ${hour < 12 ? 'am' : 'pm'}`;
}

// "8–9 pm", "11 am–12 pm"
export function hourRange(hour) {
    const next = (hour + 1) % 24;
    const sameHalf = (hour < 12) === (next < 12) && next !== 0;
    return sameHalf ? `${hour % 12 || 12}–${hourName(next)}` : `${hourName(hour)}–${hourName(next)}`;
}

// First column of the exact hourly table. The offset is kept because a daylight-saving
// change repeats an hour (two rows that would otherwise read the same), and partial hours
// (a 30-minute change) keep their real start and end.
export function hourRowLabel(row) {
    const match = /^(\d\d):(\d\d)–(\d\d):(\d\d) \((.+)\)$/.exec(String(row.label || ''));
    if (!match) return hourRange(hourOf(row));
    const [, startHour, startMinute, endHour, endMinute, zone] = match;
    const wholeHour = startMinute === '00' && endMinute === '00';
    return `${wholeHour ? hourRange(Number(startHour)) : `${startHour}:${startMinute}–${endHour}:${endMinute}`} (${zone})`;
}

// The hour of the day (0-23) an hourly row belongs to. Older v2 reports only
// carried it inside the label ("10:00–11:00 (UTC+10:00)").
export function hourOf(row) {
    if (Number.isInteger(row.hour)) return row.hour;
    const parsed = parseInt(String(row.label || '').slice(0, 2), 10);
    return Number.isInteger(parsed) && parsed >= 0 && parsed < 24 ? parsed : 0;
}

// Display names for the two special buckets the API uses.
export function categoryLabel(id, name) {
    if (id === 'top-level') return 'Top level';
    if (id === 'unclassified') return 'Removed or unknown content';
    if (id === 'multiple') return 'Appears in several places';
    return name || id;
}

const SPECIAL_CATEGORIES = ['top-level', 'multiple', 'unclassified'];

// One row per category. "clicks" = the category being opened + items opened inside it.
export function categoryRows(contents) {
    const groups = new Map();
    for (const item of contents) {
        const row = groups.get(item.categoryId) || {
            id: item.categoryId, name: categoryLabel(item.categoryId, item.categoryName),
            // The three catch-all buckets always read the same; real categories use the path the backend
            // computed (older payloads fall back to guessing it from the item's path).
            path: SPECIAL_CATEGORIES.includes(item.categoryId) ? categoryLabel(item.categoryId, item.categoryName)
                : item.categoryPath || (item.kind === 'category' ? item.path : item.path.split(' / ').slice(0, -1).join(' / ')) || categoryLabel(item.categoryId, item.categoryName),
            contentClicks: 0, categoryClicks: 0, unclassifiedClicks: 0, section: item.section || '',
        };
        row[item.kind === 'category' ? 'categoryClicks' : item.kind === 'content' ? 'contentClicks' : 'unclassifiedClicks'] += item.clicks;
        groups.set(item.categoryId, row);
    }
    // Two real categories with the same name (for example one in the hotel section and one in the area
    // section) must not look identical: add the section to the name and path of those only.
    const named = new Map();
    for (const row of groups.values()) {
        if (SPECIAL_CATEGORIES.includes(row.id)) continue;
        named.set(row.path, (named.get(row.path) || 0) + 1);
    }
    for (const row of groups.values()) {
        if (!SPECIAL_CATEGORIES.includes(row.id) && named.get(row.path) > 1 && row.section) {
            row.name = `${row.name} (${row.section})`;
            row.path = `${row.path} (${row.section})`;
            row.sectionSuffix = ` (${row.section})`;
        }
    }
    return [...groups.values()]
        // Clicks on removed content only ever sit in the 'unclassified' row; count them there too.
        .map(row => ({ ...row, clicks: row.categoryClicks + row.contentClicks + row.unclassifiedClicks }))
        .sort((a, b) => b.clicks - a.clicks || a.id.localeCompare(b.id));
}

// The same section suffix on every row of a category whose name is shared with another category, so the
// item table and the item ranking tell "Dining (Hotel)" and "Dining (Area)" apart too. The category's own
// row gets it on its name; every row gets it on its path. plainPath keeps the path without the suffix,
// so searching matches what a person typed in the CMS and not the label added here.
export function labelContents(contents, categories) {
    const suffixes = new Map(categories.filter(row => row.sectionSuffix).map(row => [row.id, row.sectionSuffix]));
    if (!suffixes.size) return contents;
    return contents.map(item => {
        const suffix = suffixes.get(item.categoryId);
        if (!suffix) return item;
        return { ...item, name: item.kind === 'category' && item.id === item.categoryId ? `${item.name}${suffix}` : item.name, path: `${item.path}${suffix}`, plainPath: item.path };
    });
}

export function validReport(report) {
    return report?.version === 2 && report.totals &&
        ['daily', 'hourly', 'contents', 'actions'].every(key => Array.isArray(report[key]));
}

export function initialTimeZone(venueId) {
    try {
        const stored = localStorage.getItem(`compendium-analytics-timezone:${venueId}`);
        if (stored) {
            new Intl.DateTimeFormat('en', { timeZone: stored }).format();
            return stored;
        }
    } catch (_) { /* Storage may be unavailable or an old zone may be invalid. */ }
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

// Everything the "When" view draws, derived from the daily and hourly rows.
//  rows:   one per date (short periods) or per weekday (long periods); cells = 24 hour totals
//  byHour: clicks per hour of the day over the whole period
export function timeModel(report) {
    const byHour = Array(24).fill(0);
    const cellsByDate = new Map();
    for (const row of report.hourly) {
        const hour = hourOf(row);
        const total = Number(row.totalClicks) || 0;
        byHour[hour] += total;
        if (!cellsByDate.has(row.date)) cellsByDate.set(row.date, Array(24).fill(0));
        cellsByDate.get(row.date)[hour] += total;
    }
    const byDate = report.daily.length <= HEATMAP_MAX_DATE_ROWS;
    let rows;
    if (byDate) {
        rows = report.daily.map(day => ({
            key: day.date, label: dayjs(day.date).format('ddd D MMM'), date: day.date,
            cells: cellsByDate.get(day.date) || Array(24).fill(0),
        }));
    } else {
        const weekdays = WEEKDAYS.map((name, index) => ({ key: name, label: name, days: 0, index, cells: Array(24).fill(0) }));
        for (const day of report.daily) {
            const target = weekdays[(dayjs(day.date).day() + 6) % 7];
            target.days += 1;
            (cellsByDate.get(day.date) || []).forEach((value, hour) => { target.cells[hour] += value; });
        }
        rows = weekdays.map(({ key, label, cells, days }) => ({ key, label: `${label}${days ? ` (${days} days)` : ''}`, cells }));
    }
    const max = Math.max(0, ...rows.flatMap(row => row.cells));
    const peakHourClicks = Math.max(0, ...byHour);
    const busiestHour = peakHourClicks > 0 ? byHour.indexOf(peakHourClicks) : null;
    const busiestDay = report.daily.reduce((best, day) => day.totalClicks > (best?.totalClicks || 0) ? day : best, null);
    return { rows, max, byHour, byDate, busiestHour, busiestDay };
}

// The calendar: one row per week (Monday to Sunday) from the first to the last day in `daily` (the server sends a row for
// every day of the period, empty or not), one square per day. Squares outside the period (before the first or after the
// last day of the first and last week) have no date, and `quietDays` lists the days without activity in order.
export function calendarModel(daily) {
    if (!daily.length) return { weeks: [], max: 0, quietDays: [], days: 0 };
    const values = new Map(daily.map(day => [day.date, Number(day.totalClicks) || 0]));
    const dayList = daily.map(day => day.date).sort();
    const first = dayjs(dayList[0]);
    const last = dayjs(dayList[dayList.length - 1]);
    const weeks = [];
    for (let monday = first.subtract((first.day() + 6) % 7, 'day'); !monday.isAfter(last); monday = monday.add(7, 'day')) {
        const cells = Array.from({ length: 7 }, (_, offset) => {
            const day = monday.add(offset, 'day');
            if (day.isBefore(first) || day.isAfter(last)) return { date: null, value: 0 };
            const date = day.format('YYYY-MM-DD');
            return { date, value: values.get(date) || 0 };
        });
        weeks.push({ label: monday.format('D MMM'), cells });
    }
    const dayCells = weeks.flatMap(week => week.cells).filter(cell => cell.date);
    const max = Math.max(0, ...dayCells.map(cell => cell.value));
    const quietDays = dayCells.filter(cell => !cell.value).map(cell => cell.date);
    return { weeks, max, quietDays, days: dayCells.length };
}

// Longest run of days in a row without activity: { length, start, end } (dates), or null when every day had some.
export function longestQuietRun(quietDays) {
    let best = null;
    let run = null;
    for (const date of quietDays) {
        run = run && dayjs(date).diff(dayjs(run.end), 'day') === 1 ? { ...run, end: date, length: run.length + 1 } : { start: date, end: date, length: 1 };
        if (!best || run.length > best.length) best = run;
    }
    return best;
}

// What the calendar shows in one sentence, so nobody has to read the squares to learn it.
export function calendarSentence(calendar, word = 'clicks') {
    if (calendar.days < 2) return '';
    const { quietDays, days } = calendar;
    if (calendar.max === 0) return `There were no ${word} on any of the ${days} days.`;
    if (!quietDays.length) return `Every one of the ${days} days had ${word}.`;
    const run = longestQuietRun(quietDays);
    const count = `${quietDays.length} of the ${days} days had no ${word}`;
    if (run.length < 2) return `${count}.`;
    const show = date => dayjs(date).format('ddd D MMM');
    return `${count}. The longest quiet stretch was ${run.length} days in a row (${show(run.start)} to ${show(run.end)}).`;
}

// The day split into four parts, for the weekday-by-time-of-day map (24 columns are too many to read).
export const TIME_BLOCKS = [
    { label: 'Night', range: '12–6 am' },
    { label: 'Morning', range: '6 am–12 pm' },
    { label: 'Afternoon', range: '12–6 pm' },
    { label: 'Evening', range: '6 pm–12 am' },
];

// Monday to Sunday, each with the activity in the four parts of the day (matching days in the period added together).
export function weekdayBlocks(report) {
    const rows = WEEKDAYS.map(name => ({ name, cells: TIME_BLOCKS.map(() => 0) }));
    for (const row of report.hourly) {
        rows[(dayjs(row.date).day() + 6) % 7].cells[Math.floor(hourOf(row) / 6)] += Number(row.totalClicks) || 0;
    }
    const max = Math.max(0, ...rows.flatMap(row => row.cells));
    let busiest = null;
    if (max > 0) {
        const day = rows.findIndex(row => row.cells.includes(max));
        busiest = { day: rows[day].name, block: TIME_BLOCKS[rows[day].cells.indexOf(max)] };
    }
    return { rows, max, busiest };
}

// How busy a square is, in words instead of numbers: 0 = nothing, 1 = quiet, 2 = busy, 3 = busiest. It is relative to the
// busiest square of the period, so a small venue still has squares at every level.
export const LEVELS = ['None', 'Quiet', 'Busy', 'Busiest'];
export function activityLevel(value, max) {
    if (!value || !max) return 0;
    const share = value / max;
    return share <= 1 / 3 ? 1 : share <= 2 / 3 ? 2 : 3;
}

// Which of `values` are the busiest squares, to be starred: those with the highest value, when they stand out (no more than three
// of them, and not every square that has something in it).
export function busiestSquares(values) {
    const max = Math.max(0, ...values);
    const same = values.filter(value => value === max).length;
    const withSomething = values.filter(value => value > 0).length;
    const standsOut = max > 0 && same <= 3 && !(same === withSomething && same > 1);
    return values.map(value => standsOut && value === max);
}

// The busiest day or days to mark on the calendar: only when they stand out (at most three, and not every day with activity).
export function busiestDays(calendar) {
    const days = calendar.weeks.flatMap(week => week.cells).filter(cell => cell.date);
    const best = days.filter(cell => calendar.max > 0 && cell.value === calendar.max);
    const withActivity = days.filter(cell => cell.value > 0).length;
    return best.length <= 3 && best.length < withActivity ? new Set(best.map(cell => cell.date)) : new Set();
}

// A part of a whole as text: "58%", with one decimal under 10% so small shares do not all read as 0 or 1.
export function shareText(value, total) {
    const percent = total ? value / total * 100 : 0;
    return `${percent === 0 || percent >= 10 ? Math.round(percent) : percent.toFixed(1)}%`;
}

// A plain-language answer to "what happened?" shown above everything else: what guests looked at most, not how many taps.
export function summarySentence(report, categories, dates, model = timeModel(report), word = 'clicks') {
    if (!report.totals.totalClicks) return '';
    const label = value => dayjs(value).format('D MMM YYYY');
    const opening = dates.start === dates.end ? `On ${label(dates.start)}` : `Between ${label(dates.start)} and ${label(dates.end)}`;
    const viewTotal = (report.totals.categoryClicks || 0) + (report.totals.contentClicks || 0) + (report.totals.unclassifiedClicks || 0);
    // 'unclassified' and 'multiple' are catch-all buckets, not categories a guest browsed.
    const top = categories.find(item => item.id !== 'unclassified' && item.id !== 'multiple' && item.clicks > 0);
    const parts = [top
        ? `${opening}, guests browsed ${top.id === 'top-level' ? 'the top level of the guest site' : top.name} the most (${shareText(top.clicks, viewTotal)} of category and item views).`
        : `${opening}, guests used your Compendium.`];
    if (report.daily.length > 1 && model.busiestDay?.totalClicks) {
        parts.push(`The busiest day was ${dayjs(model.busiestDay.date).format('dddd D MMM')}.`);
    }
    if (model.busiestHour !== null) parts.push(`${word === 'visits' ? 'Visits' : 'Clicks'} peak around ${hourRange(model.busiestHour)}.`);
    return parts.join(' ');
}
